/**
 * Declarations (rules §6), detected by the engine as in Belote (ARCHITECTURE
 * D11): a digital table knows every holding, so nothing can be misdeclared.
 *
 * Bulgarian comparison differs from the French: sequences and carrés are
 * settled separately. The side with the single best sequence scores all of
 * its sequences — and if the best is tied between the sides, no sequence
 * counts. The side with the best carré scores all of its carrés, whoever won
 * the sequences. Nothing is declared in No Trumps.
 */
import { SUITS, rankOf, suitOf } from '../../core/cards.js';
const CARRE_POINTS = { 11: 200, 9: 150, 14: 100, 10: 100, 13: 100, 12: 100 };
/** Carrés rank as the cards do in trumps: J 9 A 10 K Q. */
const CARRE_ORDER = [12, 13, 10, 14, 9, 11];
const RUN = {
    3: { kind: 'tierce', points: 20 },
    4: { kind: 'quarte', points: 50 },
    5: { kind: 'quint', points: 100 },
};
/** Carrés first, then sequences among the cards left, in the natural order
 *  7 8 9 10 J Q K A. A run longer than five is a quint of its top five. */
export function findBelotDeclarations(seat, cards) {
    const out = [];
    const used = new Set();
    for (const rank of [11, 9, 14, 10, 13, 12]) {
        const four = cards.filter((c) => rankOf(c) === rank);
        if (four.length === 4) {
            out.push({ seat, kind: 'carre', cards: four, points: CARRE_POINTS[rank] });
            for (const c of four)
                used.add(c);
        }
    }
    for (const suit of SUITS) {
        const ranks = cards
            .filter((c) => suitOf(c) === suit && !used.has(c))
            .map((c) => rankOf(c) ?? 0)
            .sort((a, b) => a - b);
        let start = 0;
        for (let i = 1; i <= ranks.length; i++) {
            if (i < ranks.length && ranks[i] === ranks[i - 1] + 1)
                continue;
            const run = ranks.slice(start, i);
            if (run.length >= 3) {
                const top = run.slice(-Math.min(run.length, 5));
                const { kind, points } = RUN[Math.min(run.length, 5)];
                out.push({ seat, kind, points, cards: top.map((r) => cards.find((c) => rankOf(c) === r && suitOf(c) === suit)) });
            }
            start = i;
        }
    }
    return out;
}
const topRank = (d) => Math.max(...d.cards.map((c) => rankOf(c) ?? 0));
/** Longer beats shorter, then the higher top card. 0 is a tie. */
export function compareSequences(a, b) {
    if (a.points !== b.points)
        return a.points - b.points;
    return topRank(a) - topRank(b);
}
export function resolveBelotDeclarations(cardsBySeat, mode, table) {
    const pointsByTeam = table.teams.map(() => 0);
    if (mode === 'NT')
        return { all: [], pointsByTeam };
    const found = cardsBySeat.flatMap((cards, seat) => findBelotDeclarations(seat, cards));
    const team = (d) => table.teamOf[d.seat];
    const seqs = found.filter((d) => d.kind !== 'carre');
    let seqTeam = null;
    if (seqs.length > 0) {
        const best = seqs.reduce((a, b) => (compareSequences(b, a) > 0 ? b : a));
        const tied = seqs.some((d) => team(d) !== team(best) && compareSequences(d, best) === 0);
        seqTeam = tied ? null : team(best);
    }
    const carres = found.filter((d) => d.kind === 'carre');
    let carreTeam = null;
    if (carres.length > 0) {
        const order = (d) => CARRE_ORDER.indexOf(rankOf(d.cards[0]) ?? 0);
        carreTeam = team(carres.reduce((a, b) => (order(b) > order(a) ? b : a)));
    }
    const all = found.map((d) => ({ ...d, counts: team(d) === (d.kind === 'carre' ? carreTeam : seqTeam) }));
    for (const d of all)
        if (d.counts)
            pointsByTeam[team(d)] += d.points;
    return { all, pointsByTeam };
}
/** What the table hears: kinds and verdicts, no cards. */
export function announcementsOf(result) {
    return result.all.map((d) => ({ seat: d.seat, kind: d.kind, points: d.points, counts: d.counts }));
}
/** Each seat's eight cards as dealt, from what it played plus what it holds. */
export function belotCardsDealt(tricks, holdings) {
    const out = holdings.map((h) => [...h]);
    for (const t of tricks)
        for (const p of t.plays)
            out[p.seat]?.push(p.card);
    return out;
}
/** The suits a Belot (King and Queen together) can be announced in. */
export function belotSuits(mode) {
    if (mode === 'NT')
        return [];
    if (mode === 'AT')
        return SUITS;
    return [mode];
}
//# sourceMappingURL=declarations.js.map